// Libs
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { User } from "firebase/auth";

// Local
import EmailCampaignDetailsPage from "./EmailCampaignDetailsPage";
import { ExistingEmailCampaign } from "../../models/email-campaign";
import { EmailCampaignStatistics } from "../../database/email-marketing";
import { useAuthStore } from "../../store/firebase-auth";
import { DbRead } from "../../database";
import { useToastMessageStore } from "../../store/toast-messages";
import { EMAIL_CAMPAIGNS_URL } from "../../urls";

interface Props {
  siteKey: string;
}

export default function EmailCampaignDetailsContainer({ siteKey }: Props) {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const firebaseUser = useAuthStore((state) => state.firebaseUser) as User;
  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );

  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState<ExistingEmailCampaign | null>(null);
  const [statistics, setStatistics] = useState<EmailCampaignStatistics | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [statisticsError, setStatisticsError] = useState<string | null>(null);

  useEffect(() => {
    if (!campaignId) {
      setError("Campaign ID is required");
      setLoading(false);
      return;
    }

    // Set up real-time subscriptions for campaign data and statistics
    setLoading(true);
    setError(null);
    setStatisticsError(null);

    // Subscribe to single campaign for real-time updates
    const unsubscribeCampaign = DbRead.emailCampaigns.subscribe({
      siteKey,
      onChange: (campaigns: ExistingEmailCampaign[]) => {
        // Find the specific campaign by ID
        const foundCampaign = campaigns.find((c) => c.id === campaignId);
        if (foundCampaign) {
          setCampaign(foundCampaign);
          setError(null);
        } else if (campaigns.length === 0) {
          // No campaigns loaded yet, keep loading
          return;
        } else {
          // Campaign not found in the list
          setError("Campaign not found");
          setCampaign(null);
        }
        setLoading(false);
      },
      onError: (error) => {
        console.error("Error subscribing to campaign:", error);
        setError("Failed to load campaign details");
        setLoading(false);

        addToastMessage({
          id: `campaign-subscription-error-${Date.now()}`,
          message: "Failed to load campaign details",
          type: "error",
          dialog: false,
        });
      },
    });

    // Load statistics (this doesn't need real-time updates as frequently)
    const loadStatistics = async () => {
      try {
        const statsResponse = await DbRead.emailCampaigns.getStatistics({
          siteKey,
          campaignId,
        });

        // Handle nested statistics response structure
        const statistics = (statsResponse as any).statistics || statsResponse;
        setStatistics(statistics);
        setStatisticsError(null);
      } catch (error) {
        console.warn("Statistics not available:", error);
        const statsErrorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load campaign statistics";
        setStatisticsError(statsErrorMessage);
        setStatistics(null);
      }
    };

    loadStatistics();

    // Set up periodic statistics refresh for campaigns in progress
    let statisticsInterval: NodeJS.Timeout | null = null;
    const startStatisticsPolling = () => {
      statisticsInterval = setInterval(() => {
        if (
          campaign &&
          (campaign.status === "sending" || campaign.status === "pending")
        ) {
          loadStatistics();
        }
      }, 5000); // Refresh every 5 seconds for campaigns in progress
    };

    // Start polling after initial load
    setTimeout(startStatisticsPolling, 1000);

    // Cleanup function
    return () => {
      unsubscribeCampaign();
      if (statisticsInterval) {
        clearInterval(statisticsInterval);
      }
    };
  }, [siteKey, campaignId, addToastMessage, campaign?.status]);

  const handleGoBack = () => {
    navigate(EMAIL_CAMPAIGNS_URL);
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="rounded-md border border-red-200 bg-red-50 p-4">
          <p className="text-red-800">{error || "Campaign not found"}</p>
          <button
            onClick={handleGoBack}
            className="mt-2 text-sm text-red-600 underline hover:text-red-800"
          >
            Go Back to Campaigns
          </button>
        </div>
      </div>
    );
  }

  return (
    <EmailCampaignDetailsPage
      siteKey={siteKey}
      campaign={campaign}
      statistics={statistics}
      statisticsError={statisticsError}
      loading={loading}
      error={error}
    />
  );
}
